// Die Suchhilfe am Suchfeld (ST-SEA-002, „PQL S1“; CM-SEA-012, NFR-UX-004,
// NFR-ACC-001). Vorgabe DESIGN vorgabe-pql-s1-12-10-2026, Abschnitt 1.
//
// Progressive Offenlegung: Wer keine Angaben kennt, sieht nur den Link. Er
// oeffnet einen NICHT-MODALEN Dialog mit sechs Beispielen, nur Text. Kein
// Fluent-Popover, aus demselben Grund wie „In der Sammlung“ (A80): dessen
// Fokuswaechter bleiben nach dem Schliessen im Body stehen und axe meldet sie.
// Esc und „Schließen“ geben den Fokus an den Link zurueck; ein Klick daneben
// schliesst, ohne den Fokus zu versetzen.

import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Button, Caption1, Caption1Strong, makeStyles, tokens, usePositioning } from '@fluentui/react-components';
import { Symbol } from '../shell/symbole.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

/** Die sechs Beispiele: Eingabe und was sie tut. EINE Stelle fuer Bildschirm und Probe. */
export const SUCHHILFE_BEISPIELE: readonly { readonly eingabe: string; readonly wirkung: string }[] = Object.freeze([
  { eingabe: 'Müller', wirkung: 'Name oder Kennung, wie bisher.' },
  { eingabe: 'stichwort:Presse', wirkung: 'Kontakte mit dem Stichwort Presse.' },
  { eingabe: 'stichwort:(Presse ODER Messe)', wirkung: 'Eines der beiden Stichworte reicht.' },
  { eingabe: 'ort:München plz:80', wirkung: 'Ort und Anfang der PLZ.' },
  { eingabe: 'haus:"Verlag Nord" rolle:Journalistin', wirkung: 'Rolle bei Verlag Nord.' },
  { eingabe: 'Müller -status:inaktiv', wirkung: 'Ohne inaktive Kontakte.' },
]);

const useStyles = makeStyles({
  flaeche: {
    zIndex: 1000,
    maxWidth: 'min(28rem, calc(100vw - 2rem))',
    padding: '0.75rem 1rem',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorTransparentStroke}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  tabHinweis: { display: 'block', margin: 0, marginBlockEnd: '0.5rem' },
  liste: {
    margin: 0,
    marginBlock: '0.5rem',
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    overflowWrap: 'anywhere',
  },
  eingabe: { fontFamily: tokens.fontFamilyMonospace },
  titel: { margin: 0 },
});

/**
 * `nurSymbol`: am schmalen Feld ein „?“-Knopf mit dem Namen „Suchhilfe“
 * (DESIGN L14-04); `className` blendet die Form je Breite ein oder aus.
 */
export function Suchhilfe({ nurSymbol = false, className }: { readonly nurSymbol?: boolean; readonly className?: string | undefined } = {}): ReactElement {
  const own = useStyles();
  const titelId = useId();
  const dialogId = useId();
  const [offen, setOffen] = useState(false);
  const ausloeser = useRef<HTMLButtonElement | null>(null);
  const flaeche = useRef<HTMLDivElement | null>(null);
  const { targetRef, containerRef } = usePositioning({ position: 'below', align: 'start', strategy: 'fixed', offset: 4 });

  // Nach dem Commit: erst dann steht der Knopf „Schließen“ im DOM.
  useEffect(() => {
    if (offen) flaeche.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [offen]);

  useEffect(() => {
    if (!offen) return undefined;
    const daneben = (ereignis: PointerEvent): void => {
      const ziel = ereignis.target as Node | null;
      if (flaeche.current?.contains(ziel) !== true && ausloeser.current?.contains(ziel) !== true) setOffen(false);
    };
    document.addEventListener('pointerdown', daneben);
    return () => document.removeEventListener('pointerdown', daneben);
  }, [offen]);

  const schliessen = (): void => {
    setOffen(false);
    ausloeser.current?.focus();
  };

  return (
    <>
      <Button
        type="button"
        appearance={nurSymbol ? 'secondary' : 'transparent'}
        size={nurSymbol ? 'medium' : 'small'}
        className={className}
        {...(nurSymbol ? { icon: <Symbol name="hilfe" />, 'aria-label': 'Suchhilfe' } : {})}
        ref={(knoten: HTMLButtonElement | null) => {
          ausloeser.current = knoten;
          targetRef.current = knoten;
        }}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-controls={offen ? dialogId : undefined}
        onClick={() => setOffen((war) => !war)}
      >
        {nurSymbol ? undefined : 'Suchhilfe'}
      </Button>
      {offen ? (
        <div
          ref={(knoten: HTMLDivElement | null) => {
            flaeche.current = knoten;
            containerRef.current = knoten;
          }}
          id={dialogId}
          role="dialog"
          aria-labelledby={titelId}
          className={own.flaeche}
          onKeyDown={(ereignis) => {
            if (ereignis.key !== 'Escape') return;
            ereignis.stopPropagation();
            schliessen();
          }}
          onBlur={(ereignis) => {
            const naechstes = ereignis.relatedTarget as Node | null;
            if (naechstes !== null && flaeche.current?.contains(naechstes) !== true && ausloeser.current?.contains(naechstes) !== true) setOffen(false);
          }}
        >
          <Caption1Strong as="h2" id={titelId} className={own.titel}>
            So grenzt du die Liste ein
          </Caption1Strong>
          <ul className={own.liste}>
            {SUCHHILFE_BEISPIELE.map((beispiel) => (
              <li key={beispiel.eingabe}>
                <span className={own.eingabe}>{beispiel.eingabe}</span> – {beispiel.wirkung}
              </li>
            ))}
          </ul>
          <Caption1 as="p" className={own.tabHinweis}>
            Tab übernimmt den markierten Vorschlag.
          </Caption1>
          <NebenKnopf type="button" size="small" onClick={schliessen}>
            Schließen
          </NebenKnopf>
        </div>
      ) : undefined}
    </>
  );
}
